import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@apollo/client'
import {
  GET_STORES, GET_PRODUCT_ASSIGNMENTS, STORE_PRODUCT_ADD, STORE_PRODUCT_REMOVE,
  type ManageStoresData, type ManageProductAssignmentsData,
} from '../../graphql/management'
import { Feedback, useFormFeedback } from './Feedback'

// Assign a product to stores with a per-store commission. Loads all stores +
// the product's current assignments (one query each). On save, diffs: ADD the
// newly-checked, REMOVE the unchecked-but-assigned. Mirrors iOS ProductAssignmentView.
type Row = { storeId: string; name: string; checked: boolean; commission: string; wasAssigned: boolean }

export default function ProductAssignment() {
  const { id } = useParams()
  const navigate = useNavigate()
  const fb = useFormFeedback()
  const { data: storesData, loading: l1 } = useQuery<ManageStoresData>(GET_STORES, { variables: { status: null } })
  const { data: assignData, loading: l2, refetch } = useQuery<ManageProductAssignmentsData>(GET_PRODUCT_ASSIGNMENTS, { variables: { id } })
  const [add] = useMutation(STORE_PRODUCT_ADD)
  const [remove] = useMutation(STORE_PRODUCT_REMOVE)
  const [rows, setRows] = useState<Row[]>([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!storesData || !assignData) return
    // Backend stores commissionRate as a fraction (0.1 = 10%) but storeProductAdd
    // *accepts* a whole-number percent (it divides by 100). So display + input are
    // in percent: convert the stored fraction back to percent when seeding an
    // existing assignment. Default for a new assignment is 15 (%).
    // Only active assignments are "current" — storeProducts also returns
    // soft-deleted (isActive: false) rows, which must NOT show as assigned.
    const current = new Map(
      assignData.product?.storeProducts.filter((sp) => sp.isActive).map((sp) => [sp.store.id, sp.commissionRate]) ?? [],
    )
    setRows(storesData.stores.map((s) => {
      const assigned = current.has(s.id)
      const stored = current.get(s.id)
      const commission = stored != null ? String(Math.round(stored * 100 * 100) / 100) : '15'
      return { storeId: s.id, name: s.name, checked: assigned, wasAssigned: assigned, commission }
    }))
  }, [storesData, assignData])

  function set(i: number, patch: Partial<Row>) {
    setRows((r) => r.map((row, idx) => idx === i ? { ...row, ...patch } : row))
  }

  async function onSave() {
    setSaving(true); fb.reset()
    const errors: string[] = []
    try {
      for (const row of rows) {
        if (row.checked && !row.wasAssigned) {
          const { data } = await add({ variables: { storeId: row.storeId, productId: id, commissionRate: parseFloat(row.commission) || 0 } })
          const m = data?.storeProductAdd?.userErrors?.[0]?.message
          if (m) errors.push(`${row.name}: ${m}`)
        } else if (!row.checked && row.wasAssigned) {
          const { data } = await remove({ variables: { storeId: row.storeId, productId: id } })
          const m = data?.storeProductRemove?.userErrors?.[0]?.message
          if (m) errors.push(`${row.name}: ${m}`)
        }
      }
      if (errors.length) fb.setError(errors.join(' · '))
      else fb.setSuccess('Assignments saved.')
      await refetch()
    } catch (err) { fb.setError(err instanceof Error ? err.message : String(err)) }
    finally { setSaving(false) }
  }

  if ((l1 || l2) && rows.length === 0) return <div className="muted">Loading…</div>

  return (
    <div className="manage-page">
      <header className="manage-header">
        <h1>Assign · {assignData?.product?.title ?? ''}</h1>
        <button className="btn-link" onClick={() => navigate('/manage/products')}>← Back</button>
      </header>
      <p className="muted small">Note: commission changes apply to newly-assigned stores. (Editing an existing store’s rate is a follow-up.)</p>

      <ul className="assign-list">
        {rows.map((row, i) => (
          <li key={row.storeId} className="assign-row">
            <label className="toggle">
              <input type="checkbox" checked={row.checked} onChange={(e) => set(i, { checked: e.target.checked })} />
              <span>{row.name}</span>
            </label>
            <span className="commission-input">
              <input value={row.commission} disabled={!row.checked} inputMode="decimal"
                onChange={(e) => set(i, { commission: e.target.value })} /> %
            </span>
          </li>
        ))}
      </ul>

      <Feedback error={fb.error} success={fb.success} />
      <div className="form-actions">
        <button className="btn-primary" onClick={onSave} disabled={saving}>{saving ? 'Saving…' : 'Save assignments'}</button>
      </div>
    </div>
  )
}
